export default {
  demo: {
    title: 'Book a demo',
    description: 'Fill in the form and we will contact you to schedule a personal demo of the AirPark portal.',
    form: {
      name: 'Name',
      company: 'Company/Organization',
      email: 'Email',
      phone: 'Phone',
      segment: 'Type of business',
      segments: {
        property: 'Property Owner',
        brf: 'Housing Cooperative',
        municipality: 'Municipality',
        event: 'Event/Arena',
        other: 'Other',
      },
      message: 'Message (optional)',
      submit: 'Send request',
      success: 'Thank you! We will get back to you shortly.',
    },
  },
  about: {
    hero: {
      title: 'About AirPark',
      description: 'AirPark is a Swedish software company developing digital business systems for professional parking management. Our platform helps property owners, housing cooperatives and municipalities streamline administration, increase utilization and create more value from existing parking spaces.',
    },
    underutilized: {
      title: 'Parking is an underutilized resource',
      description: 'Parking has long been managed with manual processes, separate systems and limited visibility. At the same time, large parts of existing parking spaces stand empty during parts of the day – while demand is high in other contexts.',
      insight: 'AirPark was founded from the insight that parking needs to be managed as a business and societal resource, not as an administrative cost.',
    },
    digitalSystem: {
      title: 'A digital business system for parking',
      description: 'AirPark offers a comprehensive digital solution that brings together the entire parking operation in one system – from permits, rentals and payments to control, data and follow-up.',
      platformTitle: 'The platform is designed to:',
      benefits: {
        reduceAdmin: 'Reduce manual administration',
        enableSharing: 'Enable shared use and subletting',
        increaseTransparency: 'Increase transparency and decision support',
        supportSustainability: 'Support more sustainable urban development',
      },
    },
    customers: {
      title: 'Developed for professional operators',
      description: 'What our customers have in common is the need for structure, control and scalability – whether it concerns a single property or an entire urban area.',
      usedBy: 'AirPark is used by:',
      types: {
        propertyOwners: 'Property owners',
        parkingCompanies: 'Parking companies',
        coops: 'Housing cooperatives',
        municipalities: 'Municipalities',
        parkingOwners: 'Parking and facility owners',
      },
    },
    journey: {
      title: 'AirPark\'s Journey',
      description: 'From idea to digital business system for parking',
      milestones: {
        founded: {
          year: '2021',
          title: 'The idea is born',
          description: 'AirPark is founded by Kim Olsson, with the vision of an "Airbnb for parking" – a platform that maximizes the utilization of parking spaces and creates new revenue for property owners, housing cooperatives and individuals.',
        },
        development: {
          year: '2022',
          title: 'Development',
          description: 'A scalable platform is developed together with industry expertise, focusing on digitalization, automation and simple parking administration.',
        },
        pilot: {
          year: '2023',
          title: 'Pilot projects & proven business',
          description: 'Successful pilot projects, including in Oceanhamnen and Rögle BK\'s event parking, confirm the platform\'s potential and the scalability of the business model.',
        },
        establishment: {
          year: '2024–2025',
          title: 'Strategic establishment',
          description: 'The platform is further developed into a comprehensive parking solution. Strategic partnerships are established and AirPark is positioned as a long-term partner for property owners, housing cooperatives and municipalities.',
        },
        today: {
          year: '2026',
          title: 'Digital business system for parking',
          description: 'AirPark is today a complete, digital and automated business system that streamlines the operation of parking portfolios and helps customers increase their net operating income with minimal effort.',
        },
      },
    },
    future: {
      title: 'Our vision for the future of parking',
      description: 'We believe the future of parking is built on:',
      points: {
        utilization: 'Better utilization of existing resources',
        digital: 'Digital management instead of new construction',
        sharing: 'Shared use between different user groups',
        environment: 'Reduced search traffic and lower environmental impact',
      },
      conclusion: 'AirPark is continuously developed to meet these needs and be a long-term partner in the development of smarter cities and properties.',
    },
    cta: {
      title: 'Want to know more?',
      description: 'Contact us and we\'ll tell you more about how AirPark can help you.',
    },
  },
  nav: {
    whoAreYou: 'Who are you?',
    products: 'Products',
    caseStudies: 'Case Studies',
    aboutAirPark: 'About AirPark',
    park: 'Park',
    faq: 'Frequently asked questions',
    news: 'News',
    portal: 'The AirPark-portal',
    individual: 'Individual',
    propertyOwner: 'Property Owner',
    coop: 'Housing Cooperative',
    event: 'Event Organizer',
    municipality: 'Municipality',
    parkingCompany: 'Parking Company',
    airparkPortal: 'AirPark Portal',
    airparkApp: 'AirPark App',
    airpass: 'AirPass',
    bookDemo: 'Book demo',
    caseOceanhamnen: 'Oceanhamnen',
    caseRogle: 'Rögle BK',
    caseLkp: 'Lund Municipality Parking',
  },
  hero: {
    title: 'The parking system that automates operations and maximizes the value of every parking space',
    subtitle: 'Consolidate all parking administration',
    subtitlePart2: 'in one system',
    description: 'AirPark is the digital business system that helps property owners, housing cooperatives and municipalities automate and streamline their entire parking operations. Increase your net operating income with minimal effort.',
    propertyOwner: 'Create new revenue and increase property value with AirPark',
    bookDemo: 'Book demo',
    learnMore: 'Explore the AirPark Portal',
    valueProps: {
      efficiency: 'Streamline administration',
      utilization: 'Higher utilization of existing parking',
      datadriven: 'Data-driven parking management',
    },
  },
  problem: {
    title: 'Parking management is often split across multiple systems',
    description: 'Today, parking is often managed manually in several different systems by different suppliers. It takes a lot of time, costs money and provides poor overview. Which often increases the risk of manual errors.',
    solutionTitle: 'AirPark – the entire parking flow, fully automated',
    solution: 'AirPark consolidates permits, rentals, payments, access and follow-up in a single system. Everything happens automatically – from booking to revenue.',
    benefits: {
      lessAdmin: 'Up to 80% less administration',
      fullControl: 'Full control over occupancy and revenue - in real-time',
      newRevenue: 'New revenue opportunities without manual work',
      onePlatform: 'One platform instead of multiple suppliers',
    },
    cta: 'Book Demo – See how AirPark automates parking.',
  },
  features: {
    title: 'Automate your entire parking business',
    subtitle: 'Everything needed to digitize, manage and streamline parking operations – from permits and rentals to payments, control and follow-up.',
    allInOne: {
      title: 'All-in-one system',
      description: 'Consolidate your entire parking operation in AirPark Portal. Dashboard, statistics, users and payments – in one shared business system.',
    },
    subletting: {
      title: 'Subletting',
      description: 'Enable tenants and residents to rent out their spaces when not in use. Increase utilization and create more value.',
    },
    priceAvailability: {
      title: 'Smart pricing',
      description: 'Control pricing based on demand and time. Optimize revenue without manual intervention.',
    },
    marketplace: {
      title: 'Marketplace',
      description: 'Sell and rent parking spaces digitally via AirPark. Enable both direct rental and subletting in a controlled and traceable flow.',
    },
    controlFees: {
      title: 'Control fees',
      description: 'Handle control fees digitally with clear follow-up and less administration.',
    },
    airparkPay: {
      title: 'Automatic payment',
      description: 'All payment handling happens automatically – from invoicing to reminders and follow-up.',
    },
    queueManagement: {
      title: 'Queue management',
      description: 'Digital management of queues and waiting lists. Transparency, automatic notifications and fair allocation.',
    },
    airpass: {
      title: 'AirPass',
      description: 'Digital access to garages and gates. Users open via the app – no keys or remote controls.',
    },
    charging: {
      title: 'EV charging',
      description: 'Manage charging stations as part of the parking business. Control availability, usage and pricing of EV charging directly in AirPark.',
    },
  },
  calculator: {
    title: 'Calculator income',
    parking: 'parking',
    parkings: 'parkings',
    hour: 'hour',
    month: 'month',
    year: 'year',
    youCanEarn: 'You can earn about',
    disclaimer: '*The number is an estimate based on average values. Of these, some should go to tax and another to fees for AirParks mediation.',
  },
  pricing: {
    title: 'Pricing',
    rentOut: {
      title: 'Rent out your parking space',
      description: 'AirPark charges a fee of 20% of the billed amount.',
      price: '20%',
    },
    permit: {
      title: 'Parking permit',
      description: 'AirPark charges a fee of 7% of the billed amount.',
      price: '7%',
    },
    controlFee: {
      title: 'Control fees',
      description: '50% of the billed amount is for AirPark.',
      price: '50%',
    },
    airpass: {
      title: 'AirPass',
      description: '*A starting fee of 7 995 SEK is required and contract period of 12 months.',
      price: '235 SEK/month*',
    },
  },
  getStarted: {
    title: 'Get started with AirPark',
    manage: {
      title: 'Manage parking spaces',
      description: 'Are you a homeowners\' association, property and land owner, property company, or municipality representative looking to streamline and improve the management of parking spaces?',
      cta: 'Read more',
    },
    rentOut: {
      title: 'Rent out parking',
      description: 'Do you have an attractive parking space that is going unused? You can rent it out to other AirPark users, to neighbors, or to customers to earn some extra money!',
      cta: 'Read more',
    },
    park: {
      title: 'Park with AirPark',
      description: 'With AirPark, you can park in an easy and convenient way. Previously unavailable parking spaces can now become available to you. Be a part of a new, greener way of parking.',
      cta: 'Read more',
    },
  },
  caseStudy: {
    title: 'Case Studies',
    oceanhamnen: {
      title: 'Oceanhamnen',
      description: 'AirPark enabled efficient use of parking spaces in a growing urban area – without new infrastructure.',
      benefits: {
        benefit1: 'New revenue from previously unused parking spaces',
        benefit2: 'Shared use between residents, visitors and businesses',
        benefit3: 'Digital management in one shared system',
      },
      cta: 'Read more about Oceanhamnen',
    },
    rogle: {
      title: 'Rögle BK',
      description: 'With AirPark, Rögle was able to manage parking digitally during high demand while improving the experience for visitors.',
      benefits: {
        benefit1: 'Smoother parking experience at events',
        benefit2: 'Quick implementation before match days',
        benefit3: 'Reduced administration for the organizer',
      },
      testimonial: 'The implementation went incredibly smoothly – everything worked from day one.',
      author: 'Robin Westerling',
      authorTitle: 'Rögle',
      cta: 'Read more about Rögle',
    },
    lkp: {
      title: 'Lund Municipality Parking (LKP)',
      subtitle: 'Nihls Bohr parking garage',
      description: 'AirPark is used as a tool to streamline parking management and accessibility in urban environments.',
      benefits: {
        benefit1: 'Digital management of parking areas',
        benefit2: 'Support for accessibility and reduced search traffic',
        benefit3: 'Collaboration between municipality and private operators',
      },
      cta: 'Read more about the collaboration',
    },
  },
  footer: {
    title: 'Join the new parking community',
    contactUs: 'Contact us',
    privacyPolicy: 'Privacy policy',
    cookiePolicy: 'Cookie policy',
    terms: 'Terms and conditions',
    cta: {
      title: 'Ready to increase your parking value?',
      description: 'Book a free demo and see how AirPark can help you automate your parking operations and increase your net operating income.',
      button: 'Book demo',
    },
  },
  common: {
    downloadApp: 'AirPark App',
    downloadAppDescription: 'The AirPark app is the interface for your parking customers. Here they can book parking, buy permits, rent out their space and manage payments – all connected to your parking area via AirPark Portal.',
    portalDescription: 'If you have multiple parking spaces or parking areas, the AirPark portal is perfect for you. It\'s easy to register and you can get started today with a simpler, more efficient and better parking solution.',
    createAccount: 'Create an account',
    cancel: 'Cancel',
    close: 'Close',
    loading: 'Loading',
  },
  appSection: {
    title: 'An app for everyone',
    subtitle: 'The interface for your parking customers',
    description: 'The AirPark app makes it easy for tenants and visitors to find, book and pay for parking – quickly and smoothly. Tenants can rent out their own space when not in use. Here they book parking, buy permits, get access to garage doors, rent out their space and manage payments – all on their own.',
    description2: 'Everything is automatically connected to your parking area via AirPark Portal, which minimizes administration and ensures full control and traceability.',
    features: {
      booking: {
        title: 'Book parking',
        description: 'Customers book and pay themselves – without manual handling',
      },
      permits: {
        title: 'Digital permits',
        description: 'Permits are purchased, managed and tracked fully digitally.',
      },
      subletting: {
        title: 'Subletting',
        description: 'Tenants rent out their space when not in use – automatically, controlled and traceable. Result: higher utilization and new revenue.',
      },
      airpass: {
        title: 'AirPass',
        description: 'Automatic access to garages and gates – linked to booking and permit.',
      },
    },
    result: 'The result: happier parking customers, less administration and a fully automated parking business.',
    cta: 'Download the app',
    ctaSubtext: 'Available for iOS and Android',
  },
  consent: {
    title: 'We use cookies',
    description: 'We use cookies to improve your experience on our website, analyze traffic and show relevant ads.',
    learnMore: 'Learn more about cookies',
    settings: 'Settings',
    necessary: 'Necessary only',
    acceptAll: 'Accept all',
    settingsTitle: 'Cookie settings',
    save: 'Save settings',
  },
  kpi: {
    title: 'AirPark in numbers',
    description: 'We help property owners and housing cooperatives maximize the value of their parking spaces.',
    parkingSpaces: 'Parking spaces',
    partners: 'Partners',
    satisfaction: 'Satisfied customers',
    revenueIncrease: 'Revenue increase',
    disclaimer: '* Numbers are examples and will be updated with real data.',
  },
  portal: {
    section: {
      label: 'AirPark Portal',
      title: 'The portal that gives you full control',
      description: 'AirPark Portal is the hub of the AirPark platform. A digital business system that consolidates rentals, permits, payments, access and follow-up in one system – giving full control over administration, utilization and revenue.',
      features: {
        dashboard: {
          title: 'Clear dashboard',
          description: 'Unified view of occupancy, revenue and status in real-time',
        },
        permits: {
          title: 'Digital parking permits & Queues',
          description: 'Automated management of permits, queues and residential parking',
        },
        subletting: {
          title: 'Direct & subletting rentals',
          description: 'Activate parking spaces when not in use',
        },
        billing: {
          title: 'Automatic billing & payments',
          description: 'Less administration and secure payment flows',
        },
        controlFees: {
          title: 'Control fees',
          description: 'Digital management of control fees and regulations',
        },
        automation: {
          title: 'Automated processes',
          description: 'Fewer manual steps – more time for other things',
        },
      },
      cta: 'Book demo',
      ctaSubtext: 'See how AirPark Portal works in practice',
      learnMore: 'Learn more about the portal',
      screenshotPlaceholder: 'Portal screenshot',
      screenshotNote: 'Add real screenshots here',
    },
  },
  beforeAfter: {
    title: 'AirPark in practice: revenue, efficiency and sustainability',
    subtitle: 'From manual administration to digital business system.',
    before: {
      title: 'Without AirPark',
      items: {
        item1: 'Manual handling of permits and waiting lists',
        item2: 'Unused parking spaces',
        item3: 'Time-consuming invoicing and follow-up',
        item4: 'No overview of occupancy and revenue',
        item5: 'Difficult to offer flexible solutions',
      },
    },
    after: {
      title: 'With AirPark',
      items: {
        item1: 'Significantly reduced administration through automation',
        item2: 'More rented spaces without manual work',
        item3: 'Revenue is generated and tracked automatically',
        item4: 'Full control and traceability in real-time',
        item5: 'New revenue opportunities through flexible and digital rentals',
      },
    },
    result: 'Less administration, better control and higher revenue – fully automated',
    closing: 'Used today by property owners, parking companies, housing cooperatives and municipalities in Sweden',
  },
  contact: {
    title: 'Contact us',
    description: 'Have questions or want to get started with AirPark? Get in touch and we\'ll help you!',
    email: 'Email',
    phone: 'Phone',
    formTitle: 'Send us a message',
    successMessage: 'Thank you for your message! We\'ll get back to you as soon as possible.',
    form: {
      name: 'Name',
      email: 'Email address',
      phone: 'Phone number',
      company: 'Company (optional)',
      subject: 'Subject',
      message: 'Message',
      submit: 'Send message',
      sending: 'Sending...',
      requiredFields: 'Required fields',
    },
    errors: {
      nameRequired: 'Name is required',
      emailRequired: 'Email address is required',
      emailInvalid: 'Invalid email address',
      subjectRequired: 'Subject is required',
      messageRequired: 'Message is required',
      messageTooShort: 'Message must be at least 10 characters',
    },
  },
  faq: {
    title: 'Frequently Asked Questions',
    description: 'Find answers to the most common questions about AirPark',
    stillHaveQuestions: 'Still have questions?',
    contactDescription: 'Can\'t find the answer you\'re looking for? Contact us and we\'ll help you!',
    categories: {
      general: 'General',
      renting: 'Renting Out',
      parking: 'Parking',
      technical: 'Technical',
    },
    general: {
      whatIsAirpark: {
        question: 'What is AirPark?',
        answer: 'AirPark is a platform that makes it easy to rent out and book parking spaces. With our app and portal, you can manage everything related to parking in a smooth way.',
      },
      howDoesItWork: {
        question: 'How does AirPark work?',
        answer: 'You download the AirPark app, create an account, and can then either rent out your parking space or book an available spot. Everything is handled digitally via the app.',
      },
      isFree: {
        question: 'Is it free to use AirPark?',
        answer: 'It\'s free to register and create an account. AirPark takes a commission on transactions: 20% for rentals, 7% for parking permit sales, and 50% of control fees.',
      },
      whoCanUse: {
        question: 'Who can use AirPark?',
        answer: 'AirPark is for everyone! Individuals, housing cooperatives, property owners, companies, and municipalities can all use our platform to manage parking.',
      },
    },
    renting: {
      howToRentOut: {
        question: 'How do I rent out my parking space?',
        answer: 'Download the AirPark app, create an account, add your parking space with photos and description, set price and availability. Then you\'re ready to go!',
      },
      howMuchEarn: {
        question: 'How much can I earn?',
        answer: 'It depends on the location and demand. Use our calculator on the homepage to get an estimate. Many earn several thousand per year on unused parking spaces.',
      },
      fees: {
        question: 'What fees does AirPark charge?',
        answer: 'AirPark charges a 20% commission on parking space rentals, 7% on parking permit sales, and 50% of control fees.',
      },
      payment: {
        question: 'How do I get paid?',
        answer: 'Payments are made automatically via the app. The money is deposited into your registered bank account within 3-5 business days after each transaction.',
      },
    },
    parking: {
      howToBook: {
        question: 'How do I book a parking space?',
        answer: 'Open the AirPark app, search for available spaces in your area, select the spot that suits you best, and confirm the booking. You\'ll then receive instructions on how to find the space.',
      },
      cancellation: {
        question: 'Can I cancel a booking?',
        answer: 'Yes, you can cancel up to 24 hours before the booking starts at no cost. For later cancellations, a fee may apply.',
      },
      payment: {
        question: 'How do I pay for parking?',
        answer: 'All payments are made securely via the app with card or Swish. You\'ll receive a receipt immediately after payment.',
      },
    },
    technical: {
      appAvailability: {
        question: 'Is the app available for both iOS and Android?',
        answer: 'Yes, the AirPark app is available for both iOS in the App Store and Android in Google Play.',
      },
      airpass: {
        question: 'What is AirPass?',
        answer: 'AirPass is a smart solution for garages and gates. With AirPass, users with valid parking permits can open and close doors directly via the app using geolocation.',
      },
      support: {
        question: 'How do I contact support?',
        answer: "You can contact our support via the app, email us at info{'@'}airpark.se, or use the contact form on the website. We typically respond within 24 hours.",
      },
    },
  },
  parkWithAirpark: {
    title: 'Park with AirPark',
    description: 'Use AirPark to easily book a parking spot in advance, save time and money while also contributing to a more sustainable future by reducing car emissions and pollution.',
    sustainableParking: {
      title: 'Environmentally-Friendly Parking',
      description: 'By using AirPark to park your car, you are contributing to a more sustainable future in multiple ways. You can easily find and book a parking spot in advance, which reduces the risk of having to drive around and search for a spot. This reduces your car emissions and helps to reduce pollution.',
    },
    flexibleOptions: {
      title: 'Flexible Parking Options',
      description: 'With AirPark, you can easily choose from different parking options depending on your needs. For example, if you only need to park for a short amount of time, you can choose a time-limited spot. If you need to park for a longer period, such as overnight or a full month, you can choose daily or monthly parking.',
    },
    centralLocations: {
      title: 'Central Locations',
      description: 'With AirPark, you can easily find a central parking spot near your destination and book it in advance, giving you the ability to easily reach your destinations without having to search for street parking. This saves you time and stress, as you already know you have a place to park when you arrive.',
    },
    parkingPermit: {
      title: 'Parking permits',
      description: 'With the AirPark app, you have access to a wide range of parking spaces and permits in the area where you live or work. Everything is easily managed within the app, so just download and start using it. Save time and stress, download the AirPark app today to find the parking that suits you best!',
    },
    moreQuestions: 'More questions?',
    moreQuestionsDescription: 'If you want to get started with AirPark, feel free to contact us! We look forward to helping you get a good start with our service. Get in touch and we\'ll tell you more and help you get started.',
  },
  individual: {
    hero: {
      title: 'Parking for Individuals',
      description: 'With AirPark, you can easily rent out your parking space or find and book parking near you. Earn money on your unused spot or find the perfect parking.',
    },
    benefits: {
      title: 'Benefits for Individuals',
      description: 'AirPark makes it easy to manage parking, whether you want to rent out or find a spot.',
    },
    features: {
      rentOut: {
        title: 'Rent Out Your Spot',
        description: 'Have a parking space sitting empty? Rent it out and earn extra money every month.',
      },
      easyBooking: {
        title: 'Easy Booking',
        description: 'Book parking in seconds directly in the app. No complicated processes.',
      },
      earnMoney: {
        title: 'Earn Money',
        description: 'Turn your unused parking space into an income source. You set the price and availability.',
      },
      secure: {
        title: 'Secure Payments',
        description: 'All payments are handled securely via the app. You get the money directly to your account.',
      },
      findParking: {
        title: 'Find Parking',
        description: 'Search among thousands of parking spaces near your destination and book in advance.',
      },
      flexible: {
        title: 'Flexible Times',
        description: 'Rent by the hour, day, or month. You choose what suits you best.',
      },
      prebook: {
        title: 'Pre-book parking',
        description: 'Skip the stress of searching for parking. Pre-book your spot in advance and know you have parking when you arrive.',
      },
    },
    howItWorks: {
      title: 'How It Works',
      step1: {
        title: 'Download the App',
        description: 'Get the AirPark app for free from the App Store or Google Play.',
      },
      step2: {
        title: 'Create an Account',
        description: 'Register in seconds with your email or phone number.',
      },
      step3: {
        title: 'Start Parking or Rent Out',
        description: 'Search for available spots or list your own space for rent.',
      },
    },
    appPreview: 'AirPark App',
    prebook: {
      title: 'Pre-book and skip the stress',
      description: 'With AirPark you can pre-book your parking spot in advance. Stop driving around searching – know you have a spot waiting for you.',
    },
    downloadApp: 'Download the app',
  },
  propertyOwner: {
    hero: {
      title: 'Parking Solutions for Property Owners',
      description: 'Streamline the management of your parking spaces with AirPark. Increase revenue, reduce administration, and give your tenants a better parking experience.',
    },
    benefits: {
      title: 'Benefits for Property Owners',
      description: 'AirPark gives you the tools to manage parking professionally and efficiently.',
      bullet1: 'Increase revenue by up to 40% through smart and flexible rentals*',
      bullet2: 'Automated invoicing and payment management',
      bullet3: 'Real-time overview of occupancy, revenue and usage',
      bullet4: 'Full control and traceability',
      bullet5: 'New revenue channels through subletting and shared use',
      disclaimer: '*Results vary depending on property and usage',
    },
    features: {
      management: {
        title: 'Easy Management',
        description: 'Manage all your parking spaces from a central portal. Overview of occupancy and revenue.',
      },
      revenue: {
        title: 'Increased Revenue',
        description: 'Maximize revenue from your parking spaces through smart pricing and rentals.',
      },
      analytics: {
        title: 'Statistics and Reports',
        description: 'Get detailed statistics on usage, revenue, and trends for your parking spaces.',
      },
      access: {
        title: 'Access Control',
        description: 'Control who has access to your parking spaces with AirPark Communities.',
      },
      permits: {
        title: 'Parking Permits',
        description: 'Sell and manage parking permits digitally. No more paperwork and manual administration.',
      },
      controlFees: {
        title: 'Control Fees',
        description: 'Issue control fees for illegally parked vehicles directly via the app.',
      },
    },
    portal: {
      title: 'AirPark Portal',
      description: 'With the AirPark Portal, you get a powerful tool to manage all aspects of your parking operations.',
      feature1: 'Overview of all parking spaces',
      feature2: 'Manage tenants and permits',
      feature3: 'Automatic billing',
      feature4: 'Detailed reports and statistics',
    },
    portalPreview: 'AirPark Portal',
  },
  coop: {
    hero: {
      title: 'Parking Solutions for Housing Cooperatives',
      description: 'Simplify parking management in your cooperative with AirPark. Handle permits, queues, and subletting smoothly.',
    },
    benefits: {
      title: 'Benefits for Housing Cooperatives',
      description: 'AirPark helps your cooperative manage parking efficiently and fairly.',
    },
    features: {
      memberManagement: {
        title: 'Member Management',
        description: 'Manage which members have access to parking spaces and permits.',
      },
      subletting: {
        title: 'Subletting',
        description: 'Let members rent out their spaces when not in use. Easy and controlled.',
      },
      waitlist: {
        title: 'Queue Management',
        description: 'Manage the queue for parking spaces fairly and transparently for all members.',
      },
      billing: {
        title: 'Automatic Billing',
        description: 'No more manual invoicing. AirPark handles payments automatically.',
      },
      communities: {
        title: 'Closed Communities',
        description: 'Create a closed parking community only for your members.',
      },
      overview: {
        title: 'Complete Overview',
        description: 'See status of all parking spaces, permits, and payments in real-time.',
      },
    },
    cta: {
      title: 'Get Started with AirPark',
      description: 'Register your cooperative today and start simplifying parking management.',
    },
  },
  event: {
    hero: {
      title: 'Parking Solutions for Event Organizers',
      description: 'Give your visitors a smooth parking experience. With AirPark, you can manage event parking, pre-booking, and rewards.',
    },
    benefits: {
      title: 'Benefits for Event Organizers',
      description: 'AirPark helps you create a better experience for your event visitors.',
    },
    features: {
      eventParking: {
        title: 'Event Parking',
        description: 'Create dedicated parking areas for your events with custom prices and times.',
      },
      prebooking: {
        title: 'Pre-booking',
        description: 'Let visitors book parking in advance to guarantee a spot.',
      },
      rewards: {
        title: 'Rewards System',
        description: 'Reward your visitors with discounts and contests via AirPark Rewards.',
      },
      capacity: {
        title: 'Capacity Management',
        description: 'Track occupancy in real-time and avoid overcrowded parking.',
      },
      payment: {
        title: 'Smooth Payment',
        description: 'Visitors pay easily via the app. No cash or ticket machines.',
      },
      digital: {
        title: 'Fully Digital',
        description: 'No physical parking tickets. Everything is handled digitally in the app.',
      },
    },
    cta: {
      title: 'Improve Your Event Parking',
      description: 'Contact us to discuss how AirPark can help your next event.',
    },
    caseStudyImage: 'Rögle BK Arena',
  },
  municipality: {
    hero: {
      title: 'Parking Solutions for Cities and Municipalities',
      description: 'Modernize municipal parking management with AirPark. Reduce emissions, increase efficiency, and improve service for citizens.',
    },
    benefits: {
      title: 'Benefits for Municipalities',
      description: 'AirPark helps municipalities create smarter and more sustainable parking solutions.',
    },
    features: {
      publicParking: {
        title: 'Public Parking',
        description: 'Manage municipal parking spaces digitally with real-time monitoring.',
      },
      sustainability: {
        title: 'Sustainability',
        description: 'Reduce emissions by helping drivers find parking faster.',
      },
      data: {
        title: 'Data-Driven Planning',
        description: 'Use parking data to make better decisions about urban planning.',
      },
      charging: {
        title: 'Charging Infrastructure',
        description: 'Integrate EV charging stations into the parking solution.',
      },
      permits: {
        title: 'Digital Permits',
        description: 'Manage residential parking and other permits fully digitally.',
      },
      integration: {
        title: 'System Integration',
        description: 'Integrate with existing municipal systems and services.',
      },
    },
    stats: {
      emissions: 'Reduced emissions from parking search',
      efficiency: 'Increased efficiency in parking management',
      availability: 'Availability for citizens',
    },
    cta: {
      title: 'Modernize Municipal Parking',
      description: 'Contact us to discuss how AirPark can help your municipality.',
    },
  },
  airparkPortal: {
    hero: {
      title: 'AirPark Portal',
      description: 'The complete tool for managing parking spaces, permits, and revenue. Perfect for property owners, cooperatives, and businesses.',
    },
    overview: {
      title: 'Everything You Need in One Place',
      description: 'AirPark Portal gives you full control over your parking operations with powerful tools and detailed statistics.',
    },
    features: {
      dashboard: {
        title: 'Clear Dashboard',
        description: 'See all important information in one place. Occupancy, revenue, and activity in real-time.',
      },
      mapping: {
        title: 'Mapping',
        description: 'Visualize your parking spaces on an interactive map.',
      },
      users: {
        title: 'User Management',
        description: 'Manage users, permits, and permissions easily.',
      },
      billing: {
        title: 'Billing',
        description: 'Automatic invoicing and payment management.',
      },
      reports: {
        title: 'Reports',
        description: 'Generate detailed reports for analysis and follow-up.',
      },
      settings: {
        title: 'Settings',
        description: 'Customize the portal to your needs and preferences.',
      },
    },
    getStarted: {
      title: 'Get Started in Minutes',
      step1: {
        title: 'Create an Account',
        description: 'Register for free at portal.airpark.app.',
      },
      step2: {
        title: 'Add Parking Spaces',
        description: 'Map your parking spaces and set prices.',
      },
      step3: {
        title: 'Start Earning Money',
        description: 'Publish your spaces and start receiving bookings.',
      },
    },
    screenshot: 'AirPark Portal Dashboard',
    freeToStart: 'Free to get started!',
    problemsSolved: {
      title: 'What problems does AirPark Portal solve?',
      problem: {
        subtitle: 'Manual and inefficient parking administration?',
        intro: 'Many property owners and housing cooperatives manage parking through:',
        list: {
          excel: 'Excel spreadsheets',
          queues: 'Manual waiting lists',
          permits: 'Physical permits',
          payments: 'Separate payment solutions',
          admin: 'Time-consuming administration',
        },
        leadsTo: 'This leads to:',
        consequences: {
          unnecessaryAdmin: 'Unnecessary administration',
          lackOfOverview: 'Lack of overview',
          missedRevenue: 'Missed revenue',
          sublettingControl: 'Difficulty controlling subletting',
          insufficientData: 'Insufficient data on occupancy and revenue',
        },
        solution: 'AirPark Portal digitalizes the entire process.',
      },
      solution: {
        subtitle: 'Increase revenue from existing parking areas',
        intro: 'AirPark Portal makes it possible to:',
        list: {
          activate: 'Activate unused parking spaces',
          subletting: 'Create new revenue streams through subletting',
          charging: 'Charge for EV charging',
          pricing: 'Optimize pricing based on demand',
        },
        conclusion: 'A parking space that stands empty is lost revenue',
      },
    },
    targetAudience: {
      title: 'Who is AirPark Portal for?',
      brf: {
        title: 'Housing Cooperatives',
        description: 'Manage the association\'s parking spaces with digital queue management and controlled subletting.',
      },
      commercial: {
        title: 'Commercial Property Owners',
        description: 'Optimize revenue from parking areas with automated rental and billing.',
      },
      parking: {
        title: 'Parking Companies',
        description: 'Complement existing operations with digital permits and rental.',
      },
      municipality: {
        title: 'Municipalities',
        description: 'Digital management of parking permits and monitoring of parking inventory.',
      },
    },
  },
  airparkApp: {
    hero: {
      title: 'AirPark App',
      subtitle: 'The future of parking – in your hand.',
      description: 'Find, pre-book and pay for parking, or rent out your space when it\'s not in use. All in the AirPark app.',
    },
    overview: {
      title: 'All your parking. In one app.',
      description: 'AirPark brings the entire parking experience into one digital flow – from planning to payment and access. Pre-book parking in advance, manage your parking permit and share your space when you\'re not using it. It\'s parking – upgraded.',
    },
    appFeatures: {
      findParking: {
        title: 'Find & pre-book your parking',
        description: 'Stop circling block after block. With AirPark, you find available parking spaces in attractive locations and book them directly in the app – before you\'ve even started the car. Plan your parking as easily as you plan your meeting.',
      },
      bookAndPay: {
        title: 'Book and pay – fully digital',
        description: 'Everything happens in the app. Payment, receipt and booking confirmation are handled automatically – without parking meters or extra apps. You see your active bookings, history and payments all in one place. Convenient. Secure. Time-saving.',
      },
      earnMoney: {
        title: 'Earn money from your parking space',
        description: 'When your parking space is empty, it can work for you. Make it available in the app with one tap and rent it out when you\'re not using it. You set the price and availability – we handle booking and payment. An unused space becomes a new source of income.',
      },
      digitalPermit: {
        title: 'Your parking permit – fully digital',
        description: 'Forget physical permits in the windshield. With AirPark, you have your parking permit right in your phone – always updated and always accessible. Manage your permit, check validity and sublet your space when you\'re not using it. All administration is digital. Easier for you – more efficient for the property owner.',
      },
    },
    smarterParking: {
      title: 'A smarter way to park',
      subtitle: 'Stop searching – Start planning',
      description1: 'A Swedish driver spends hundreds of hours searching for parking. We think there are better things to do with your time.',
      description2: 'With AirPark, you find and pre-book parking near your destination – in advance. You pay directly in the app and avoid meters, stress and uncertainty.',
      description3: 'Have your own space? Let it work for you when it\'s empty. Rent it out safely via the app and get the income directly to your account.',
      description4: 'Your parking permit is digital, always updated and always with you.',
      conclusion: 'One app. Full control. Smarter for you – better for the city.',
    },
    mockup: 'AirPark App',
    features: {
      findParking: {
        title: 'Find Parking',
        description: 'Search among thousands of parking spaces and find the perfect spot.',
      },
      booking: {
        title: 'Easy Booking',
        description: 'Book parking in seconds. Choose time and place that suits you.',
      },
      payment: {
        title: 'Secure Payment',
        description: 'Pay easily with card or Swish directly in the app.',
      },
      rentOut: {
        title: 'Rent Out',
        description: 'Earn money by renting out your parking space when you\'re not using it.',
      },
      airpass: {
        title: 'AirPass Integration',
        description: 'Open garages and gates directly from the app with AirPass.',
      },
      notifications: {
        title: 'Notifications',
        description: 'Get reminders about bookings and when your parking is about to expire.',
      },
    },
  },
  airpass: {
    hero: {
      title: 'AirPass',
      description: 'Open garages and gates directly from the AirPark app. A smart and secure solution for modern parking access.',
    },
    howItWorks: {
      title: 'How AirPass Works',
      description: 'AirPass uses geolocation and secure identification to give authorized users access to garages and parking facilities.',
      step1: {
        title: 'Installation',
        description: 'AirPass is installed on your garage door or gate.',
      },
      step2: {
        title: 'Connection',
        description: 'The device is connected to the AirPark platform.',
      },
      step3: {
        title: 'Usage',
        description: 'Users with valid permits open the gate via the app.',
      },
    },
    features: {
      access: {
        title: 'Smooth Access',
        description: 'Open gates and garages directly from your phone. No keys or remotes.',
      },
      geolocation: {
        title: 'Geolocation',
        description: 'The app detects when you\'re nearby and makes it easy to open the gate.',
      },
      security: {
        title: 'High Security',
        description: 'Secure identification ensures only authorized users get access.',
      },
      instant: {
        title: 'Instant Access',
        description: 'No waiting. The gate opens immediately when you tap in the app.',
      },
      app: {
        title: 'Integrated in the App',
        description: 'AirPass is fully integrated in the AirPark app. No extra app needed.',
      },
      installation: {
        title: 'Easy Installation',
        description: 'AirPass is quickly and easily installed on most gates and doors.',
      },
    },
    pricing: {
      title: 'Pricing',
      description: 'AirPass is a cost-effective solution for modern access control.',
    },
  },
  news: {
    title: 'News',
    description: 'Stay updated with the latest news from AirPark',
    readMore: 'Read more',
    readTime: 'read',
    locale: 'en-US',
    categories: {
      product: 'Product',
      partnership: 'Partnership',
      sustainability: 'Sustainability',
      company: 'Company',
      tips: 'Tips & Advice',
    },
    articles: {
      article1: {
        title: 'New Feature: AirPark Communities Launches',
        excerpt: 'We\'re excited to introduce AirPark Communities - a new feature that enables housing cooperatives and property owners to create closed parking communities.',
      },
      article2: {
        title: 'AirPark Partners with Rikshem',
        excerpt: 'We\'re proud to announce our new partnership with Rikshem, one of Sweden\'s leading property companies. Together we will revolutionize parking across the country.',
      },
      article3: {
        title: 'How AirPark Contributes to a Greener Future',
        excerpt: 'By optimizing the use of existing parking spaces, we reduce the need for new construction and contribute to more sustainable urban development.',
      },
      article4: {
        title: 'AirPass - The Parking Solution of the Future',
        excerpt: 'With AirPass, you can open garages and gates directly from the app. Learn more about how this smart solution simplifies everyday life for both property owners and users.',
      },
      article5: {
        title: 'AirPark Grows - 50,000 Users',
        excerpt: 'We\'re celebrating an important milestone! AirPark now has over 50,000 registered users and continues to grow rapidly across Sweden.',
      },
      article6: {
        title: '5 Tips to Maximize Revenue from Your Parking Space',
        excerpt: 'Want to earn more from your unused parking space? Here are our best tips for optimizing price, availability, and attractiveness.',
      },
    },
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated',
    sections: {
      intro: {
        title: 'Introduction',
        content: 'AirPark AB ("we", "us", "our") values your privacy. This policy describes how we collect, use and protect your personal data in accordance with GDPR and Swedish data protection legislation.',
      },
      dataController: {
        title: 'Data Controller',
        content: 'AirPark AB is the data controller for the processing of your personal data.',
      },
      dataCollected: {
        title: 'What data do we collect?',
        content: 'We collect the following types of personal data:',
        items: {
          contact: 'Contact information (name, email, phone)',
          company: 'Company information (company name, organization number)',
          usage: 'Usage data (how you use our services)',
          technical: 'Technical data (IP address, browser, device)',
        },
      },
      purpose: {
        title: 'Why do we process your data?',
        content: 'We process your personal data for the following purposes:',
        items: {
          service: 'Provide and improve our services',
          communication: 'Communicate with you about our services',
          improvement: 'Analyze and improve user experience',
          legal: 'Fulfill legal obligations',
        },
      },
      rights: {
        title: 'Your rights',
        content: 'Under GDPR, you have the following rights:',
        items: {
          access: 'Right of access - request information about what data we have about you',
          rectification: 'Right to rectification - correct inaccurate data',
          erasure: 'Right to erasure - request that we delete your data',
          restriction: 'Right to restriction - limit how we use your data',
          portability: 'Right to data portability - receive your data in machine-readable format',
          objection: 'Right to object - object to certain processing',
        },
      },
      thirdParty: {
        title: 'Third-party services',
        content: 'We use the following third-party services that may process your personal data:',
      },
      contact: {
        title: 'Contact us',
        content: 'If you have questions about how we process your personal data or want to exercise your rights, contact us.',
      },
    },
  },
  cookies: {
    title: 'Cookie Policy',
    lastUpdated: 'Last updated',
    table: {
      service: 'Service',
      purpose: 'Purpose',
      type: 'Type',
      analytics: 'Web analytics and statistics',
      marketing: 'Marketing and tracking',
      tagManager: 'Tag management',
    },
    sections: {
      what: {
        title: 'What are cookies?',
        content: 'Cookies are small text files stored on your device when you visit our website. They help us improve your experience and understand how the website is used.',
      },
      types: {
        title: 'Types of cookies we use',
        necessary: {
          title: 'Necessary cookies',
          description: 'These cookies are necessary for the website to function properly. They cannot be turned off.',
          status: 'Always active',
        },
        functional: {
          title: 'Functional cookies',
          description: 'These cookies enable enhanced functionality and personalization, such as language settings.',
          status: 'Requires consent',
        },
        analytics: {
          title: 'Analytics cookies',
          description: 'These cookies help us understand how visitors interact with the website by collecting anonymous information.',
          status: 'Requires consent',
        },
        marketing: {
          title: 'Marketing cookies',
          description: 'These cookies are used to display relevant ads and measure the effectiveness of marketing campaigns.',
          status: 'Requires consent',
        },
      },
      thirdParty: {
        title: 'Third-party cookies',
        content: 'We use the following third-party services that may set cookies on your device:',
      },
      manage: {
        title: 'Manage your cookie settings',
        content: 'You can change your cookie settings at any time by clicking the button below.',
        button: 'Manage cookies',
      },
      contact: {
        title: 'Contact us',
        content: 'If you have questions about our use of cookies, contact us.',
      },
    },
  },
  targetAudience: {
    title: 'Who are you?',
    subtitle: 'AirPark offers tailored solutions for different types of businesses',
    learnMore: 'Learn more',
    disclaimer: '* Results vary depending on property and usage',
    propertyOwner: {
      tab: 'Property Owners',
      title: 'Property Owners',
      description: 'Turn parking into a business resource with a digital business system. AirPark consolidates rentals, permits, payments and follow-up in one platform – giving you full control over utilization and revenue.',
      benefit1: 'Increase revenue by up to 40% through smart rentals*',
      benefit2: 'Automated invoicing and payment management',
      benefit3: 'Real-time overview of occupancy and revenue',
    },
    municipality: {
      tab: 'Municipalities',
      title: 'Municipalities',
      description: 'AirPark is a digital management system for municipal parking. The platform consolidates permits, residential parking, accessibility and follow-up in one system – providing better control, reduced administration and data-driven decisions.',
      benefit1: 'Reduce search traffic and emissions in the city center',
      benefit2: 'Digital management of residential parking and permits',
      benefit3: 'Data-driven planning for future mobility',
    },
    parkingCompany: {
      tab: 'Parking Companies',
      title: 'Parking Companies',
      description: 'Expand and streamline your offering with a digital business system. AirPark complements traditional parking operations with rentals, permits, payments and customer management – in one shared platform.',
      benefit1: 'Complements parking surveillance with digital rentals and permits',
      benefit2: 'Integrated management of customers, cases and payments',
      benefit3: 'Scalable platform that easily adapts to new areas and assignments',
    },
    coop: {
      tab: 'Housing Cooperatives',
      title: 'Housing Cooperatives',
      description: 'Create more value from your association\'s parking spaces through controlled and digital shared use. AirPark enables residents to rent out their spaces when not in use – while the association maintains full control and shares in the revenue.',
      benefit1: 'Subletting of parking spaces – safe and regulated',
      benefit2: 'New revenue for both residents and association',
      benefit3: 'Full overview of permits, usage and revenue',
    },
  },
  parkingCompany: {
    hero: {
      title: 'Parking Solutions for Parking Companies',
      description: 'AirPark gives parking companies a modern, digital platform to streamline their entire parking operation – from permit handling and rentals to access, payment and follow-up.',
    },
    benefits: {
      title: 'Benefits for Parking Companies',
      description: 'AirPark gives you full control over the entire parking business in one system.',
    },
    features: {
      permits: {
        title: 'Digital permit handling',
        description: 'Sell and manage permits digitally. No more manual administration – everything is handled automatically in the portal.',
      },
      access: {
        title: 'AirPass access',
        description: 'Give customers access to garages and gates via the app. No keys or remote controls needed.',
      },
      payment: {
        title: 'Automatic payment',
        description: 'All payments are handled automatically – from invoicing to reminders. You get full control and overview.',
      },
      statistics: {
        title: 'Statistics and reports',
        description: 'Get detailed statistics on revenue, occupancy and trends. Make data-driven decisions.',
      },
      control: {
        title: 'Control and follow-up',
        description: 'Handle control fees digitally with clear traceability and less administration.',
      },
      queue: {
        title: 'Queue handling',
        description: 'Digital management of queues and waiting lists. Transparency and automatic notifications.',
      },
    },
    portal: {
      title: 'Get started with AirPark Portal',
      description: 'Streamline your parking operation with our comprehensive digital platform. Book a demo to see how AirPark can help your parking company.',
    },
  },
}

